import { FunctionComponent, ReactElement } from 'react'
import {
  Box,
  colors,
  TableCell,
  TableRow,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import {
  SiteRFPDetails,
  SiteRFPMilestoneTypeAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import {
  CheckCircleRounded,
  PendingActionsRounded,
  ScheduleRounded,
} from '@mui/icons-material'
import { formatTableDate } from 'components/helpers/formatTableDate'

type Milestone = SiteRFPDetails['milestones'][number]

interface MilestoneRowProps {
  milestone: Milestone
}

function getStatusIcon(milestone: Milestone): ReactElement | null {
  if (milestone.actualDate != null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <CheckCircleRounded
          sx={{
            color: rucksack.palette.success.main,
            height: '20px',
            width: '20px',
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: rucksack.palette.text.secondary }}
        >
          Approved
        </Typography>
      </Box>
    )
  }
  if (milestone.submittedDate != null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <PendingActionsRounded
          sx={{ color: colors.ore[500], height: '20px', width: '20px' }}
        />
        <Typography
          variant="body2"
          sx={{ color: rucksack.palette.text.secondary }}
        >
          Pending
        </Typography>
      </Box>
    )
  }
  if (milestone.estimatedDate != null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <ScheduleRounded
          sx={{
            color: rucksack.palette.action.active,
            height: '20px',
            width: '20px',
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: rucksack.palette.text.secondary }}
        >
          Estimated
        </Typography>
      </Box>
    )
  }
  return null
}

function getDisplayName(
  milestoneType: SiteRFPMilestoneTypeAPIResponse
): string {
  switch (milestoneType) {
    case SiteRFPMilestoneTypeAPIResponse.RFPRelease:
      return 'Release'
    case SiteRFPMilestoneTypeAPIResponse.RFPAward:
      return 'Award'
    case SiteRFPMilestoneTypeAPIResponse.ContractEffectiveDate:
      return 'Contract Effective'
    case SiteRFPMilestoneTypeAPIResponse.Interconnection:
      return 'Interconnection Approved'
    case SiteRFPMilestoneTypeAPIResponse.Permits:
      return 'Permits'
    case SiteRFPMilestoneTypeAPIResponse.ProgramAward:
      return 'Program Award'
    case SiteRFPMilestoneTypeAPIResponse.NTP:
      return 'Notice to Proceed (NTP)'
    case SiteRFPMilestoneTypeAPIResponse.Construction:
      return 'Construction Start'
    case SiteRFPMilestoneTypeAPIResponse.MechanicalCompletion:
      return 'Mechanical Completion'
    case SiteRFPMilestoneTypeAPIResponse.PTO:
      return 'Permission to Operate (PTO)'
    case SiteRFPMilestoneTypeAPIResponse.COD:
      return 'Commercial Operation Date (COD)'
  }
}

function formatDate(milestone: Milestone) {
  if (milestone.actualDate) {
    return formatTableDate(milestone.actualDate)
  } else if (milestone.submittedDate) {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Typography variant="body2">
          {formatTableDate(milestone.submittedDate)}
        </Typography>
        <Typography
          sx={{ color: rucksack.palette.text.secondary }}
          variant="body2"
        >
          Submitted
        </Typography>
      </Box>
    )
  } else if (milestone.estimatedDate) {
    return (
      <Typography
        variant="body2"
        sx={{ color: rucksack.palette.text.secondary }}
      >
        ~ {formatTableDate(milestone.estimatedDate)}
      </Typography>
    )
  } else {
    return '—'
  }
}

export const MilestoneRow: FunctionComponent<MilestoneRowProps> = ({
  milestone,
}) => {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">
          {getDisplayName(milestone.type)}
        </Typography>
      </TableCell>
      <TableCell>{getStatusIcon(milestone)}</TableCell>
      <TableCell>{formatDate(milestone)}</TableCell>
    </TableRow>
  )
}

MilestoneRow.displayName = 'MilestoneRow'
